import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import {
  Camera,
  HandMetal,
  Loader2,
  Check,
  X as XIcon,
  Keyboard,
  ArrowLeftRight,
  Package,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import api from '@/lib/api';

/**
 * ScanPassModal — recipient-side of the Pass flow.
 *
 * Two steps:
 *   1. Scan the QR (camera) OR paste the token manually
 *   2. Preview the offered track/crate → pick target crate → Accept
 *
 * For Trade mode we branch into offering a card back from the user's own
 * collection. For Crate mode we show a checklist so the recipient can
 * cherry-pick which tracks to accept.
 *
 * Plays a subtle chime on successful accept as the "arrived" feedback.
 */
export default function ScanPassModal({ open, onClose, onAccepted, initialToken = null }) {
  const [step, setStep] = useState('scan'); // 'scan' | 'preview' | 'trade-offer'
  const [token, setToken] = useState('');
  const [manualToken, setManualToken] = useState('');
  const [scanning, setScanning] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [preview, setPreview] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [targetBucket, setTargetBucket] = useState('');
  const [availableBuckets, setAvailableBuckets] = useState([]);
  const [selectedCards, setSelectedCards] = useState(new Set());
  // Trade counter-offer state
  const [myCards, setMyCards] = useState([]);
  const [myCardsQuery, setMyCardsQuery] = useState('');
  const [loadingMyCards, setLoadingMyCards] = useState(false);
  const [offering, setOffering] = useState(false);

  const scannerRef = useRef(null);
  const scannerElId = 'pass-scan-region';

  // Reset on open. If we received an initialToken (from deep-link
  // /pass/{token}), auto-load its preview so the user lands directly in
  // the accept flow instead of the scanner.
  useEffect(() => {
    if (open) {
      setStep('scan');
      setToken('');
      setManualToken('');
      setPreview(null);
      setSelectedCards(new Set());
      setTargetBucket('');
      // Fetch user's buckets once so we can offer a crate picker.
      api
        .get('/cards/albums')
        .then(({ data }) => {
          const buckets = (data?.albums || []).map((a) => a.genre_bucket);
          setAvailableBuckets(buckets);
          if (initialToken) {
            // Small delay so buckets are populated before handleToken picks
            // a sensible default target_bucket.
            setTimeout(() => handleToken(initialToken), 50);
          }
        })
        .catch(() => {
          setAvailableBuckets([]);
          if (initialToken) setTimeout(() => handleToken(initialToken), 50);
        });
    } else {
      stopScanner();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialToken]);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      } catch (_) { /* ignore */ }
      scannerRef.current = null;
      setScanning(false);
    }
  }, []);

  // Extract just the token from any of the QR value shapes we generate.
  // We currently generate: `${origin}/pass/{token}` — but also gracefully
  // accept raw tokens for the manual-entry fallback.
  const extractToken = (raw) => {
    if (!raw) return null;
    const s = String(raw).trim();
    const match = s.match(/\/pass\/([A-Za-z0-9_-]+)/);
    if (match) return match[1];
    if (/^[A-Za-z0-9_-]{8,64}$/.test(s)) return s;
    return null;
  };

  const startScanner = useCallback(async () => {
    setScanning(true);
    // Small delay so the <div> renders before Html5Qrcode grabs it.
    await new Promise((r) => setTimeout(r, 100));
    try {
      const scanner = new Html5Qrcode(scannerElId);
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decodedText) => {
          const tok = extractToken(decodedText);
          if (tok) {
            stopScanner();
            handleToken(tok);
          }
        },
        () => { /* scan errors are silent — happens constantly while searching */ },
      );
    } catch (e) {
      toast.error('Could not access camera. Try manual entry.');
      setScanning(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToken = useCallback(async (tok) => {
    setToken(tok);
    setLoadingPreview(true);
    try {
      const { data } = await api.get(`/pass/${tok}/preview`);
      setPreview(data);
      // Default target crate: sender's original bucket if we already have
      // a crate by that name, else the first user crate, else empty.
      const proposed = data?.track?.genre_bucket || data?.crate?.bucket || '';
      const hasMatch = availableBuckets.includes(proposed);
      setTargetBucket(hasMatch ? proposed : (availableBuckets[0] || ''));
      // Preselect all cards for crate mode.
      if (data?.mode === 'crate') {
        setSelectedCards(new Set((data?.crate?.tracks || []).map((t) => t.card_id)));
      }
      setStep('preview');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Invalid or expired Pass');
      setToken('');
    } finally {
      setLoadingPreview(false);
    }
  }, [availableBuckets]);

  const playChime = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, now);
      osc.frequency.exponentialRampToValueAtTime(1320, now + 0.12);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.4);
    } catch (_) { /* audio blocked — silent */ }
  };

  const handleAccept = useCallback(async () => {
    if (!token) return;
    setAccepting(true);
    try {
      const body = { target_bucket: targetBucket || null };
      if (preview?.mode === 'crate') {
        body.selected_card_ids = Array.from(selectedCards);
      }
      const { data } = await api.post(`/pass/${token}/accept`, body);
      playChime();
      toast.success(
        preview?.mode === 'crate'
          ? `Added ${data?.cards_added ?? 0} tracks to ${targetBucket}`
          : 'Track added to your collection',
      );
      onAccepted?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to accept Pass');
    } finally {
      setAccepting(false);
    }
  }, [token, targetBucket, preview, selectedCards, onAccepted, onClose]);

  // Trade flow: recipient picks one of their own cards to offer back.
  const startTradeOffer = useCallback(async () => {
    setStep('trade-offer');
    setLoadingMyCards(true);
    try {
      const { data } = await api.get('/cards', {
        params: { status: 'active' },
      });
      setMyCards(data?.cards || []);
    } catch (_) {
      toast.error('Could not load your tracks');
    } finally {
      setLoadingMyCards(false);
    }
  }, []);

  const submitTradeOffer = useCallback(async (cardId) => {
    if (!token) return;
    setOffering(true);
    try {
      await api.post(`/pass/${token}/offer-back`, { card_id: cardId });
      toast.success('Counter-offer sent. Waiting for the sender to accept…');
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send counter-offer');
    } finally {
      setOffering(false);
    }
  }, [token, onClose]);

  const filteredMyCards = useMemo(() => {
    if (!myCardsQuery.trim()) return myCards;
    const q = myCardsQuery.toLowerCase();
    return myCards.filter(
      (c) =>
        c.title?.toLowerCase().includes(q) ||
        c.artist?.toLowerCase().includes(q) ||
        c.genre_bucket?.toLowerCase().includes(q),
    );
  }, [myCards, myCardsQuery]);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          stopScanner();
          onClose?.();
        }
      }}
    >
      <DialogContent
        className="max-w-md p-0 gap-0 overflow-hidden max-h-[92vh] flex flex-col"
        data-testid="scan-pass-modal"
      >
        <DialogHeader className="px-4 pt-4 pb-3 border-b border-border">
          <DialogTitle className="flex items-center gap-2 text-base">
            <HandMetal className="w-4 h-4 text-[hsl(var(--primary))]" />
            {step === 'scan' && 'Scan a Pass'}
            {step === 'preview' && 'Incoming Pass'}
            {step === 'trade-offer' && 'Offer a track back'}
          </DialogTitle>
          <DialogDescription className="text-xs leading-relaxed">
            {step === 'scan' && 'Point your camera at a GroovLenser\u2019s Pass QR.'}
            {step === 'preview' && preview?.sender_username && `From @${preview.sender_username}`}
            {step === 'trade-offer' && 'Pick a track from your collection to trade for the incoming one.'}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto p-4">
          {step === 'scan' && (
            <div className="space-y-3">
              {!scanning ? (
                <>
                  <Button
                    onClick={startScanner}
                    className="w-full rounded-lg h-10"
                    data-testid="scan-camera-button"
                  >
                    <Camera className="w-4 h-4 mr-1.5" />
                    Open camera
                  </Button>
                  <div className="relative flex items-center gap-2">
                    <div className="flex-1 h-px bg-border" />
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">or</div>
                    <div className="flex-1 h-px bg-border" />
                  </div>
                  <div>
                    <Label
                      htmlFor="manual-token"
                      className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono"
                    >
                      Paste token
                    </Label>
                    <div className="mt-1.5 flex gap-2">
                      <Input
                        id="manual-token"
                        value={manualToken}
                        onChange={(e) => setManualToken(e.target.value)}
                        placeholder="paste QR token here"
                        className="h-9 text-sm flex-1"
                        data-testid="scan-manual-token-input"
                      />
                      <Button
                        onClick={() => {
                          const tok = extractToken(manualToken);
                          if (tok) handleToken(tok);
                          else toast.error('Not a valid Pass token');
                        }}
                        disabled={!manualToken.trim() || loadingPreview}
                        size="sm"
                        className="rounded-lg"
                        data-testid="scan-manual-submit"
                      >
                        <Keyboard className="w-3.5 h-3.5 mr-1.5" />
                        Use
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div id={scannerElId} className="rounded-xl overflow-hidden bg-black min-h-[280px]" />
                  <Button
                    variant="outline"
                    onClick={stopScanner}
                    className="w-full rounded-lg"
                    data-testid="scan-stop-button"
                  >
                    <XIcon className="w-3.5 h-3.5 mr-1.5" />
                    Cancel
                  </Button>
                </>
              )}
              {loadingPreview && (
                <div className="text-center text-xs text-muted-foreground flex items-center justify-center gap-2 mt-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Loading Pass…
                </div>
              )}
            </div>
          )}

          {step === 'preview' && preview && (
            <div className="space-y-4">
              {/* Track / crate summary */}
              {preview.mode !== 'crate' && preview.track && (
                <div className="rounded-lg border border-border p-3 flex items-center gap-3">
                  {preview.track.album_art_url && (
                    <img
                      src={preview.track.album_art_url}
                      alt=""
                      className="w-16 h-16 rounded object-cover shrink-0"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{preview.track.title || 'Untitled'}</div>
                    <div className="text-xs text-muted-foreground truncate">{preview.track.artist || ''}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-1">
                      {preview.mode === 'trade' ? (
                        <span className="text-[hsl(var(--primary))]"><ArrowLeftRight className="w-2.5 h-2.5 inline mr-0.5" />Trade</span>
                      ) : (
                        <span>{preview.track.genre_bucket}</span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {preview.mode === 'crate' && preview.crate && (
                <div className="space-y-2">
                  <div className="rounded-lg border border-border p-3 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium flex items-center gap-1.5">
                        <Package className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
                        {preview.crate.bucket}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {preview.crate.card_count} tracks · {selectedCards.size} selected
                      </div>
                    </div>
                    <div className="flex gap-1.5">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-[10px]"
                        onClick={() => setSelectedCards(new Set((preview.crate?.tracks || []).map((t) => t.card_id)))}
                      >
                        All
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-[10px]"
                        onClick={() => setSelectedCards(new Set())}
                      >
                        None
                      </Button>
                    </div>
                  </div>
                  <ScrollArea className="max-h-56 rounded-lg border border-border">
                    <div className="p-1.5 space-y-1">
                      {(preview.crate?.tracks || []).map((t) => {
                        const sel = selectedCards.has(t.card_id);
                        return (
                          <button
                            key={t.card_id}
                            type="button"
                            onClick={() => {
                              setSelectedCards((prev) => {
                                const n = new Set(prev);
                                if (n.has(t.card_id)) n.delete(t.card_id);
                                else n.add(t.card_id);
                                return n;
                              });
                            }}
                            className={`w-full flex items-center gap-2 p-2 rounded-md text-left ${
                              sel ? 'bg-[hsl(var(--primary))]/10' : 'hover:bg-secondary/50'
                            }`}
                            data-testid={`crate-track-select-${t.card_id}`}
                          >
                            <div
                              className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                                sel
                                  ? 'bg-[hsl(var(--primary))] border-[hsl(var(--primary))] text-white'
                                  : 'border-border'
                              }`}
                            >
                              {sel && <Check className="w-3 h-3" />}
                            </div>
                            {t.album_art_url && (
                              <img src={t.album_art_url} alt="" className="w-8 h-8 rounded object-cover shrink-0" />
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-medium truncate">{t.title || 'Untitled'}</div>
                              <div className="text-[10px] text-muted-foreground truncate">{t.artist || ''}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* Target crate picker */}
              {preview.mode !== 'trade' && (
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
                    File into crate
                  </Label>
                  <select
                    value={targetBucket}
                    onChange={(e) => setTargetBucket(e.target.value)}
                    className="mt-1.5 w-full h-9 rounded-lg border border-border bg-background text-sm px-2"
                    data-testid="scan-target-bucket"
                  >
                    <option value="">Use sender\u2019s bucket</option>
                    {availableBuckets.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Actions */}
              {preview.mode === 'trade' ? (
                <div className="flex gap-2">
                  <Button
                    onClick={startTradeOffer}
                    className="rounded-lg flex-1"
                    data-testid="scan-trade-offer-button"
                  >
                    <ArrowLeftRight className="w-3.5 h-3.5 mr-1.5" />
                    Offer a track back
                  </Button>
                  <Button
                    variant="outline"
                    onClick={onClose}
                    className="rounded-lg"
                  >
                    <XIcon className="w-3.5 h-3.5 mr-1.5" />
                    Cancel
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button
                    onClick={handleAccept}
                    disabled={accepting || (preview.mode === 'crate' && selectedCards.size === 0)}
                    className="rounded-lg flex-1"
                    data-testid="scan-accept-button"
                  >
                    {accepting ? (
                      <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <Check className="w-3.5 h-3.5 mr-1.5" />
                    )}
                    {preview.mode === 'crate' ? `Add ${selectedCards.size} track${selectedCards.size === 1 ? '' : 's'}` : 'Accept Pass'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={onClose}
                    disabled={accepting}
                    className="rounded-lg"
                  >
                    <XIcon className="w-3.5 h-3.5 mr-1.5" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {step === 'trade-offer' && (
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  value={myCardsQuery}
                  onChange={(e) => setMyCardsQuery(e.target.value)}
                  placeholder="Search your tracks…"
                  className="pl-8 h-9 text-sm"
                  data-testid="trade-offer-search"
                />
              </div>
              {loadingMyCards ? (
                <div className="text-center text-xs text-muted-foreground flex items-center justify-center gap-2 py-8">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Loading your tracks…
                </div>
              ) : (
                <ScrollArea className="max-h-72 rounded-lg border border-border">
                  <div className="p-1.5 space-y-1">
                    {filteredMyCards.length === 0 && (
                      <div className="text-center text-xs text-muted-foreground py-6">
                        No tracks match.
                      </div>
                    )}
                    {filteredMyCards.slice(0, 200).map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => submitTradeOffer(c.id)}
                        disabled={offering}
                        className="w-full flex items-center gap-2 p-2 rounded-md text-left hover:bg-secondary/50 disabled:opacity-50"
                        data-testid={`trade-offer-card-${c.id}`}
                      >
                        {c.album_art_url && (
                          <img src={c.album_art_url} alt="" className="w-9 h-9 rounded object-cover shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-medium truncate">{c.title || 'Untitled'}</div>
                          <div className="text-[10px] text-muted-foreground truncate">{c.artist || ''} · {c.genre_bucket}</div>
                        </div>
                        <ArrowLeftRight className="w-3.5 h-3.5 text-[hsl(var(--primary))] shrink-0" />
                      </button>
                    ))}
                  </div>
                </ScrollArea>
              )}
              <Button
                variant="outline"
                onClick={() => setStep('preview')}
                disabled={offering}
                className="w-full rounded-lg"
              >
                <XIcon className="w-3.5 h-3.5 mr-1.5" />
                Back
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
